import React, { useState, useEffect, useCallback } from "react";
import images from "../../../constant/images";
import { Nav } from "../../../layouts/navbar/nav";
import Sidebar from "../../../layouts/sidebar/sidebar";
import axios from "axios";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function PaimentInfo() {
  const { i18n, t } = useTranslation();
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };
  const [user, setUser] = useState(null);
  const [amount, setAmount] = useState(null);
  const [membershipPack, setMembershipPack] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRequestLoading, setIsRequestLoading] = useState(false);
  const [error, setError] = useState(null);

  const [transactionId, setTransactionId] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");

  const [validationErrors, setValidationErrors] = useState({}); // For input validation errors

  const location = useLocation();
  const navigate = useNavigate();
  const { id, price, pack } = location.state || {}; // Ensure `id` is safely accessed
  const baseUrl = useSelector((state) => state.gtm.baseUrl);

  // Fetch user and calculate amount
  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      const token = localStorage.getItem("userToken");
      if (token) {
        const response = await axios.get(`${baseUrl}/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);

        if (response.data.membership?.status === "valid") {
          const newPrice =
            price - response.data.membership?.membership_packs?.price;
          setAmount(newPrice);
        } else {
          setAmount(price);
        }
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      setError("Failed to fetch user data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl, price]);

  // Validate inputs
  const validateInputs = () => {
    const errors = {};
    if (!transactionId.trim()) {
      errors.transactionId = "Transaction ID is required.";
    } else if (isNaN(transactionId)) {
      errors.transactionId = "Transaction ID must be a number.";
    }
    if (!bankName.trim()) {
      errors.bankName = "Bank Name is required.";
    }
    if (!accountHolderName.trim()) {
      errors.accountHolderName = "Account Holder Name is required.";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0; // Return true if no errors
  };

  // Submit payment request
  const sendRequest = async () => {
    if (!validateInputs()) {
      return; // Stop submission if validation fails
    }

    try {
      setIsRequestLoading(true);
      const response = await axios.post(
        `${baseUrl}/requests`,
        {
          user_id: user?.id,
          transaction_id: transactionId,
          bank_name: bankName,
          name: accountHolderName,
          pack: pack,
          price: amount,
        },
        {
          withCredentials: true,
          withXSRFToken: true,
          credentials: "include",
        }
      );

      if (response.status === 200) {
        navigate("/membership/thanks", {
          state: {
            pack: pack,
            price: amount,
          },
        });
      }
    } catch (error) {
      console.error("Error submitting payment request:", error);
      setError(
        error.response?.data?.message ||
          "Unable to send request. Please try again later."
      );
    } finally {
      setIsRequestLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading) {
    return (
      <div className="bg-white shadow-custom bg-opacity-[87%] h-full flex flex-col justify-center items-center p-6 md:p-16 gap-4 md:gap-8 w-full md:w-[100%] rounded-3xl py-8 md:py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-custom bg-opacity-[87%] h-full flex flex-col justify-start items-center p-6 md:p-16 gap-4 md:gap-8 w-full md:w-[100%] rounded-3xl py-8 md:py-12">
      <div className="text-center text-2xl md:text-4xl font-bold mb-2 md:mb-3">
        <p> {t("payment_info.payment")}</p>
      </div>
      <div className="text-center flex flex-col gap-3 md:gap-4 leading-relaxed md:leading-loose">
        <div className="text-lg md:text-xl mb-2 md:mb-4 font-semibold">
          {t("payment_info.payment_instructions")}
        </div>

        <div className="text-sm md:text-base">
          <span className="font-bold"> {t("payment_info.company")}:</span> GOLF
          TRIPS MOROCCO SARL
        </div>
        <div className="text-sm md:text-base">
          <span className="font-bold">RIB :</span>{" "}
          <span className="font-semibold text-green-500">
            011780000083210000500438
          </span>
        </div>
        <div className="text-sm md:text-base">
          <span className="font-bold">IBAN :</span> MA64
          011780000083210000500438
        </div>
        <div className="text-sm md:text-base">
          <span className="font-bold">SWIFT :</span> BMCEMAMC
        </div>

        <div className="flex flex-col md:flex-row gap-3 md:gap-6 w-full items-center justify-center">
          <div className="text-sm md:text-base">
            <span className="font-bold">Pack :</span>{" "}
            <span className="font-bold">{pack || "N/A"}</span>
          </div>
          <div className="text-sm md:text-base">
            <span className="font-bold"> {t("payment_info.amount")}:</span>{" "}
            {amount || 0} MAD TTC
          </div>
        </div>

        <div className="flex flex-col gap-2 md:gap-3 items-center text-start justify-center w-full">
          <div className="text-start flex flex-col gap-1 w-full md:w-[80%]">
            <label className="font-semibold text-sm md:text-base">
              Transaction ID
            </label>
            <input
              type="text"
              value={transactionId}
              onChange={(e) => {
                setTransactionId(e.target.value);
                setValidationErrors((prev) => ({
                  ...prev,
                  transactionId: null,
                }));
              }}
              className="bg-[#F4F5F9] border-none rounded-xl py-3 px-5 w-full max-w-full sm:max-w-[500px] mx-auto block placeholder-[#A4A4A4]"
              placeholder="Transaction ID"
            />
            {validationErrors.transactionId && (
              <div className="text-red-500 text-sm mt-1">
                {validationErrors.transactionId}
              </div>
            )}
          </div>
          <div className="text-start flex flex-col gap-1 w-full md:w-[80%]">
            <label className="font-semibold text-sm md:text-base">
              {t("payment_info.bank_name")}
            </label>
            <input
              type="text"
              value={bankName}
              onChange={(e) => {
                setBankName(e.target.value);
                setValidationErrors((prev) => ({ ...prev, bankName: null }));
              }}
              className="bg-[#F4F5F9] border-none rounded-xl py-3 px-5 w-full max-w-full sm:max-w-[500px] mx-auto block placeholder-[#A4A4A4]"
              placeholder="Bank Name"
            />
            {validationErrors.bankName && (
              <div className="text-red-500 text-sm mt-1">
                {validationErrors.bankName}
              </div>
            )}
          </div>
          <div className="text-start flex flex-col gap-1 w-full md:w-[80%]">
            <label className="font-semibold text-sm md:text-base">
              {t("payment_info.account_holder_name")}
            </label>
            <input
              type="text"
              value={accountHolderName}
              onChange={(e) => {
                setAccountHolderName(e.target.value);
                setValidationErrors((prev) => ({
                  ...prev,
                  accountHolderName: null,
                }));
              }}
              className="bg-[#F4F5F9] border-none rounded-xl py-3 px-5 w-full max-w-full sm:max-w-[500px] mx-auto block placeholder-[#A4A4A4]"
              placeholder="Account Holder Name"
            />
            {validationErrors.accountHolderName && (
              <div className="text-red-500 text-sm mt-1">
                {validationErrors.accountHolderName}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 md:mt-8">
          <button
            type="submit"
            onClick={sendRequest}
            disabled={isRequestLoading}
            className="w-full md:max-w-[350px] py-2 md:py-3 text-sm md:text-base rounded-[33px] bg-black font-semibold border-none cursor-pointer mx-auto block hover:bg-[#333] text-white"
          >
            {isRequestLoading ? "Processing..." : `${t("payment_info.submit")}`}
          </button>
        </div>

        {error && (
          <div className="text-red-500 mt-2 md:mt-4 font-semibold text-sm md:text-base">
            {error}
          </div>
        )}
      </div>
    </div>
  );

  // return (
  //   <div
  //     className="flex flex-col gap-12 min-h-screen"
  //     style={{
  //       backgroundImage: `url(${images.golf_field})`,
  //       backgroundPosition: "center",
  //       backgroundSize: "cover",
  //       backgroundRepeat: "no-repeat",
  //       backgroundAttachment: "fixed",
  //     }}
  //   >
  //     <Nav />
  //     <div className="flex justify-center mx-16 mb-20">
  //       <div className="flex w-full justify-between items-start">
  //         <Sidebar />
  //         <div className="bg-white shadow-custom bg-opacity-[87%] h-full flex flex-col justify-start items-center p-16 gap-8 w-[75%] rounded-3xl py-12">
  //           <div className="text-center text-4xl font-bold mb-3">
  //             <p>Payment</p>
  //           </div>
  //           <div className="text-center flex flex-col gap-4 leading-loose">
  //             <div className="text-xl mb-4 font-semibold">
  //               Please proceed with making a bank transfer to the following RIB:
  //             </div>
  //             <div>
  //               <span className="font-bold">Company :</span> GOLF TRIPS MOROCCO
  //               SARL
  //             </div>
  //             <div>
  //               <span className="font-bold">RIB :</span>{" "}
  //               <span className="font-semibold text-green-500">
  //                 011780000083210000500438
  //               </span>
  //             </div>
  //             <div>
  //               <span className="font-bold">IBAN :</span> MA64
  //               011780000083210000500438
  //             </div>
  //             <div>
  //               <span className="font-bold">SWIFT :</span> BMCEMAMC
  //             </div>
  //             <div className="flex gap-6 w-full items-center justify-center">
  //               <div>
  //                 <span className="font-bold ">Pack : <span>{pack || "N/A"}</span></span>{" "}

  //               </div>
  //               <div>
  //                 <span className="font-bold">Amount :</span> {amount || 0} MAD
  //                 TTC
  //               </div>
  //             </div>

  //             <div className="flex flex-col gap-3 items-center text-start justify-center w-full">
  //               <div className="text-start flex flex-col gap-1 w-[80%]">
  //                 <label className="font-semibold">Transaction ID</label>
  //                 <input
  //                   type="text"
  //                   value={transactionId}
  //                   onChange={(e) => setTransactionId(e.target.value)}
  //                   className="bg-[#F4F5F9] border-none rounded-xl py-3 px-5 w-full max-w-full sm:max-w-[500px] mx-auto block placeholder-[#A4A4A4]"
  //                   placeholder="Transaction ID"
  //                 />
  //               </div>
  //               <div className="text-start flex flex-col gap-1 w-[80%]">
  //                 <label className="font-semibold">Bank Name</label>
  //                 <input
  //                   type="text"
  //                   value={bankName}
  //                   onChange={(e) => setBankName(e.target.value)}
  //                   className="bg-[#F4F5F9] border-none rounded-xl py-3 px-5 w-full max-w-full sm:max-w-[500px] mx-auto block placeholder-[#A4A4A4]"
  //                   placeholder="Bank Name"
  //                 />
  //               </div>
  //               <div className="text-start flex flex-col gap-1 w-[80%]">
  //                 <label className="font-semibold">Account Holder Name</label>
  //                 <input
  //                   type="text"
  //                   value={accountHolderName}
  //                   onChange={(e) => setAccountHolderName(e.target.value)}
  //                   className="bg-[#F4F5F9] border-none rounded-xl py-3 px-5 w-full max-w-full sm:max-w-[500px] mx-auto block placeholder-[#A4A4A4]"
  //                   placeholder="Account Holder Name"
  //                 />
  //               </div>
  //             </div>
  //             <div className="mt-8">
  //               <button
  //                 type="submit"
  //                 onClick={sendRequest}
  //                 disabled={isRequestLoading}
  //                 className="w-full sm:max-w-[350px] py-3 text-base rounded-[33px] bg-black font-semibold border-none cursor-pointer mx-auto block hover:bg-[#333] text-white"
  //               >
  //                 {isRequestLoading ? "Processing..." : "Submit"}
  //               </button>
  //             </div>
  //             {error && (
  //               <div className="text-red-500 mt-4 font-semibold">{error}</div>
  //             )}
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   </div>
  // );
}
